import { expandTabBlock, findTabBlocks, isNamedOrnamentRow } from './tab-line.js'

/**
 * True when the text is ASCII tab and nothing else. Spec 2026-08-25 §6, amended §7a.
 *
 * Block-aware rather than a line count. The original implementation counted matching lines across
 * the whole paste and capped at six, so a two-stanza tab (12 rows) failed and fell silently to the
 * song path.
 *
 * A multi-stanza paste is ONE tab, not one per stanza — reversed 2026-08-30. "Exactly one block"
 * was right while a single element fused every stanza onto one continuous line, because splitting
 * was the only way the boundary survived. `2026-08-29` §4a makes a source break a forced break
 * inside one element, so splitting buys nothing now and costs the blank lines between stanzas,
 * which end up belonging to no element at all.
 *
 * What decides it is therefore what sits BETWEEN the blocks, not how many there are: blanks and a
 * block's own ornament rows are part of the tab, and a lyric or a chord line means these are two
 * separate tabs with something in between, which is the song path. Same rule `chart-parser`'s
 * `group.ts` already applies when it absorbs blanks between stanzas.
 */
export function isAsciiTab(tab: string): boolean {
  const lines = tab.split('\n')
  const blocks = findTabBlocks(lines)
  if (blocks.length === 0) {
    return false
  }
  // Expand first. Spec §7a: a tab's palm-mute, count and `Tuning:` rows are neither string rows nor
  // blank, so checking before expanding sent every ornament-carrying tab to the song path — where
  // the tokenizer normalised the text before §7's absorption could ever run.
  const spans = blocks.map(block => expandTabBlock(lines, block))
  /**
   * Everything outside every block must be blank or a named ornament row, or this is a chart, not a
   * bare tab.
   *
   * The ornament clause is §7a read literally — it defines a pure tab as blocks plus blank lines
   * plus ornament rows, and this function implemented only the first two. `expandTabBlock` absorbs
   * an ornament row only when it is flush against a block, which is correct and deliberate: a blank
   * line terminates a block. So a count row separated from its system by a blank line — the shape
   * in the report that prompted this, where `x5` and `x4` stand alone between systems — belongs to
   * no span, is not blank, and dropped the entire paste to the song path.
   *
   * Named patterns only, never the alphabetic-ratio fallback. That fallback is safe inside
   * `expandTabBlock` because adjacency is already established there; here there is no adjacency
   * constraint, so it would swallow a chord line and fuse two separate tabs. `C  G  Am  F` passes
   * the ratio and must not pass this.
   */
  return lines.every(
    (line, index) =>
      line.trim() === '' ||
      spans.some(span => index >= span.start && index <= span.end) ||
      isNamedOrnamentRow(line)
  )
}
